import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, timeAgo, formatErr } from "../lib/api";
import RelatedContent from "../components/RelatedContent";
import { useAuth } from "../context/AuthContext";
import VoteWidget from "../components/VoteWidget";
import Markdown from "../components/Markdown";
import TranslatedText, { useTranslatedText } from "../components/TranslatedText";

function TrMarkdown({ text }) {
    return <Markdown>{useTranslatedText(text)}</Markdown>;
}
import MarkdownEditor from "../components/MarkdownEditor";
import { Spinner, BookmarkSimple, CheckCircle, Lightning, ChatCircle, Lock, PushPin, Quotes } from "@phosphor-icons/react";
import { toast } from "sonner";
import ModMenu from "../components/ModMenu";
import ReportModal from "../components/ReportModal";
import Reactions from "../components/Reactions";
import TLDRButton from "../components/TLDRButton";
import SEOHead, { makeDescription, buildBreadcrumbSchema, mergeSchemas } from "../components/SEOHead";

export default function QuestionPage({ onOpenAI, setAIPrefill }) {
    const { qid } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [answer, setAnswer] = useState("");
    const [comment, setComment] = useState({});
    const [bookmarked, setBookmarked] = useState(false);
    const [reportTarget, setReportTarget] = useState(null); // { type, id }
    const [gone, setGone] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/questions/${qid}`);
            setData(data);
        } catch (err) {
            if (err?.response?.status === 404 || err?.response?.status === 410) setGone(true);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setGone(false); load(); }, [qid]);

    const submitAnswer = async (e) => {
        e.preventDefault();
        if (!user) return toast.error("Sign in to answer");
        try {
            await api.post(`/questions/${qid}/answers`, { body: answer });
            setAnswer("");
            toast.success("Answer posted");
            load();
        } catch (err) { toast.error(formatErr(err)); }
    };

    const submitComment = async (parentId, e) => {
        e.preventDefault();
        if (!user) return toast.error("Sign in to comment");
        const body = comment[parentId];
        if (!body?.trim()) return;
        try {
            await api.post(`/comments/${parentId}`, { body });
            setComment((c) => ({ ...c, [parentId]: "" }));
            load();
        } catch (err) { toast.error(formatErr(err)); }
    };

    const accept = async (aid) => {
        try { await api.post(`/answers/${aid}/accept`); load(); }
        catch (e) { toast.error(formatErr(e)); }
    };

    const toggleBookmark = async () => {
        if (!user) return toast.error("Sign in to bookmark");
        try {
            const { data } = await api.post(`/bookmarks/${qid}`);
            setBookmarked(data.bookmarked);
            toast.success(data.bookmarked ? "Saved" : "Removed");
        } catch (e) { toast.error(formatErr(e)); }
    };

    const askAI = () => {
        if (setAIPrefill) setAIPrefill(data.question.title + "\n\n" + data.question.body);
        if (onOpenAI) onOpenAI();
    };

    const quoteReply = (a) => {
        // Prefix each non-empty line of the quoted body with "> " (markdown blockquote),
        // cap to 30 lines / 1500 chars to keep replies readable.
        const src = (a.body || "").split("\n").filter(Boolean).slice(0, 30).join("\n").slice(0, 1500);
        const quoted = src.split("\n").map((l) => `> ${l}`).join("\n");
        const prefix = `> **@${a.author_username} wrote:**\n${quoted}\n\n`;
        setAnswer((cur) => (cur ? `${cur}\n\n${prefix}` : prefix));
        // Scroll to the answer textarea
        setTimeout(() => {
            const el = document.querySelector('[data-testid="answer-input"]');
            if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); }
        }, 50);
    };

    if (gone) {
        return (
            <div className="max-w-2xl mx-auto py-16 text-center" data-testid="question-tombstone">
                <SEOHead title="Question removed" description="This question is no longer available." path={`/q/${qid}`} noindex />
                <div className="font-mono text-5xl font-black text-slate-700 mb-3">410</div>
                <h1 className="text-xl font-bold text-slate-200 mb-2">This question was removed</h1>
                <p className="text-sm text-slate-500 mb-6 font-mono">// deleted by a moderator or its author — it won't be coming back</p>
                <Link to="/q" data-testid="tombstone-browse-link"
                      className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-cyan-300 hover:text-cyan-200 border border-cyan-500/40 rounded-sm px-3 h-9 transition-colors">
                    browse live questions →
                </Link>
            </div>
        );
    }
    if (!data) return <div className="flex justify-center py-16"><Spinner size={24} className="animate-spin text-cyan-400" /></div>;
    // Merged question: backend returns merged_into + an empty answer/comment list.
    // We render a 1-card redirect notice so prior URLs don't 404 but the user gets
    // straight to the canonical question.
    if (data.merged_into) {
        return (
            <div className="border border-amber-500/30 bg-amber-500/[0.06] rounded-sm p-6 text-center" data-testid="question-merged-notice">
                <h1 className="text-lg font-bold text-amber-200" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                    This question was merged
                </h1>
                <p className="text-amber-100/80 text-sm mt-2 font-mono">
                    Canonical answers live on the merged question. We kept this URL alive for the link history.
                </p>
                <Link
                    to={`/q/${data.merged_into.question_id}`}
                    className="inline-block mt-4 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold text-[10px] uppercase tracking-widest px-5 py-2.5 rounded-sm transition-colors"
                    data-testid="question-merged-hop"
                >
                    open: {data.merged_into.title?.slice(0, 60) || "the canonical question"} →
                </Link>
            </div>
        );
    }
    const q = data.question;
    const qLocked = !!q.locked;
    const qDeleted = !!q.deleted_at;
    const accepted = (data.answers || []).find((a) => a.accepted);
    // Schema.org QAPage — gives Google rich-result eligibility for Q&A snippets.
    // Only emitted when at least one answer exists: QAPage requires an
    // acceptedAnswer or suggestedAnswer, so unanswered threads emit nothing.
    const qaSchema = (qDeleted || (data.answers || []).length === 0) ? null : {
        "@context": "https://schema.org",
        "@type": "QAPage",
        "mainEntity": {
            "@type": "Question",
            "name": q.title,
            "text": makeDescription(q.body, 500),
            "answerCount": (data.answers || []).length,
            "upvoteCount": q.score || 0,
            "dateCreated": q.created_at,
            "author": { "@type": "Person", "name": q.author_username },
            ...(accepted && {
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": makeDescription(accepted.body, 500),
                    "upvoteCount": accepted.score || 0,
                    "dateCreated": accepted.created_at,
                    "author": { "@type": "Person", "name": accepted.author_username },
                    "url": `https://hacking.community/q/${q.question_id}#a-${accepted.answer_id}`,
                },
            }),
            ...((data.answers || []).length > 0 && {
                "suggestedAnswer": data.answers.slice(0, 5).filter((a) => !a.accepted).map((a) => ({
                    "@type": "Answer",
                    "text": makeDescription(a.body, 300),
                    "upvoteCount": a.score || 0,
                    "dateCreated": a.created_at,
                    "author": { "@type": "Person", "name": a.author_username },
                })),
            }),
        },
    };

    return (
        <div data-testid="question-page">
            <SEOHead
                title={q.title}
                description={makeDescription(q.body)}
                path={`/q/${q.question_id}`}
                type="article"
                jsonLd={mergeSchemas(qaSchema, buildBreadcrumbSchema([
                    { name: "Q&A", path: "/q" },
                    { name: q.title },
                ]))}
                noindex={qDeleted}
            />
            <div className="flex flex-col lg:flex-row gap-5 items-start">
                <div className="flex-1 min-w-0 space-y-6">
            <div className="border-b border-white/10 pb-4">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {q.pinned && <span className="text-[10px] uppercase tracking-widest text-amber-400 flex items-center gap-1"><PushPin size={11} weight="fill" /> pinned</span>}
                    {qLocked && <span className="text-[10px] uppercase tracking-widest text-rose-400 flex items-center gap-1"><Lock size={11} weight="fill" /> locked</span>}
                    {qDeleted && <span className="text-[10px] uppercase tracking-widest text-rose-400">deleted</span>}
                </div>
                <div className="flex items-start gap-3">
                    <h1 className={`flex-1 text-2xl sm:text-3xl font-black tracking-tight leading-tight ${qDeleted ? "line-through text-slate-500" : "text-slate-100"}`} style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                        <TranslatedText text={q.title} />
                    </h1>
                    <ModMenu
                        targetType="question"
                        targetId={q.question_id}
                        isLocked={qLocked}
                        isPinned={q.pinned}
                        isDeleted={qDeleted}
                        onChange={load}
                        onReport={() => setReportTarget({ type: "question", id: q.question_id })}
                    />
                </div>
                <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500 font-mono">
                    <span>asked by <Link to={`/u/${q.author_username}`} className="text-cyan-400 hover:text-cyan-300" data-testid="q-author-link">{q.author_username}</Link></span>
                    <span>·</span>
                    <span>{timeAgo(q.created_at)}</span>
                    <span>·</span>
                    <span>{q.view_count || 0} views</span>
                    <button onClick={askAI} className="ml-auto flex items-center gap-1 text-purple-400 hover:text-purple-300" data-testid="q-ask-ai-btn">
                        <Lightning size={12} weight="fill" /> Explain with SEC-OPS
                    </button>
                    <button onClick={toggleBookmark} className={`flex items-center gap-1 ${bookmarked ? "text-cyan-400" : "text-slate-400 hover:text-cyan-400"}`} data-testid="q-bookmark-btn">
                        <BookmarkSimple size={12} weight={bookmarked ? "fill" : "regular"} /> {bookmarked ? "Saved" : "Save"}
                    </button>
                </div>
            </div>

            <article className="flex gap-4 hc-card p-5">
                <VoteWidget targetType="question" targetId={qid} initialScore={q.score} />
                <div className="flex-1 min-w-0">
                    <div data-testid="q-body">
                        <TrMarkdown text={q.body} />
                    </div>
                    <TLDRButton content={q.body} kind="question" />
                    <div className="flex flex-wrap gap-1.5 mt-4">
                        {(q.tags || []).map((t) => (
                            <Link key={t} to={`/?tag=${t}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-800 text-slate-300 border border-slate-700 hover:border-cyan-500/40 rounded-sm">#{t}</Link>
                        ))}
                    </div>
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/8">
                        <Reactions targetId={qid} />
                    </div>
                </div>
            </article>

            {data.comments?.length > 0 && (
                <div className="ml-14 space-y-1.5">
                    {data.comments.map((c) => (
                        <div key={c.comment_id} className="text-xs text-slate-400 font-mono border-l border-white/10 pl-3 py-1">
                            {c.body} <span className="text-slate-600">— <Link to={`/u/${c.author_username}`} className="text-cyan-500/70 hover:text-cyan-400">{c.author_username}</Link> · {timeAgo(c.created_at)}</span>
                        </div>
                    ))}
                </div>
            )}
            {user && (
                <form onSubmit={(e) => submitComment(qid, e)} className="ml-14 flex gap-2">
                    <input
                        value={comment[qid] || ""}
                        onChange={(e) => setComment((c) => ({ ...c, [qid]: e.target.value }))}
                        placeholder="add a comment..."
                        className="flex-1 bg-transparent border-b border-white/10 text-xs text-slate-300 font-mono py-1 focus:border-cyan-500 focus:outline-none"
                        data-testid="q-comment-input"
                    />
                    <button type="submit" className="text-[10px] uppercase tracking-widest text-cyan-400 hover:text-cyan-300">post</button>
                </form>
            )}

            <div className="pt-4 border-t border-white/10">
                <h2 className="text-lg font-bold tracking-tight text-slate-200 flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                    <ChatCircle size={18} weight="duotone" className="text-cyan-400" />
                    {data.answers.length} {data.answers.length === 1 ? "Answer" : "Answers"}
                </h2>
            </div>

            <div className="space-y-4">
                {data.answers.map((a) => (
                    <article key={a.answer_id} className={`flex gap-4 border rounded-sm p-5 ${a.accepted ? "bg-emerald-500/5 border-emerald-500/30" : "bg-[#131a2a] border-white/10"} ${a.deleted_at ? "opacity-50" : ""}`} data-testid={`answer-${a.answer_id}`}>
                        <div className="flex flex-col items-center gap-2">
                            <VoteWidget targetType="answer" targetId={a.answer_id} initialScore={a.score} />
                            {a.accepted && <CheckCircle size={20} weight="fill" className="text-emerald-400" />}
                            {!a.accepted && user?.user_id === q.author_id && (
                                <button onClick={() => accept(a.answer_id)} className="text-slate-600 hover:text-emerald-400" title="Accept answer" data-testid={`accept-${a.answer_id}`}>
                                    <CheckCircle size={20} weight="regular" />
                                </button>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex-1 min-w-0">
                                    {a.deleted_at ? <Markdown>_[content removed by moderator]_</Markdown> : <TrMarkdown text={a.body} />}
                                    <TLDRButton content={a.body} kind="answer" />
                                </div>
                                <ModMenu
                                    targetType="answer"
                                    targetId={a.answer_id}
                                    isDeleted={!!a.deleted_at}
                                    onChange={load}
                                    onReport={() => setReportTarget({ type: "answer", id: a.answer_id })}
                                />
                            </div>
                            <div className="flex items-center justify-between mt-4 text-xs text-slate-500 font-mono gap-3 flex-wrap">
                                <div>
                                    <Link to={`/u/${a.author_username}`} className="text-cyan-400 hover:text-cyan-300">{a.author_username}</Link>
                                    <span> · {timeAgo(a.created_at)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Reactions targetId={a.answer_id} />
                                    {user && !a.deleted_at && (
                                        <button
                                            type="button"
                                            onClick={() => quoteReply(a)}
                                            data-testid={`quote-${a.answer_id}`}
                                            className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-cyan-400 border border-white/10 hover:border-cyan-500/40 px-2 py-1 rounded-sm flex items-center gap-1"
                                            title="Quote in your reply"
                                        >
                                            <Quotes size={11} weight="duotone" /> Quote
                                        </button>
                                    )}
                                </div>
                            </div>
                            {a.comments?.length > 0 && (
                                <div className="mt-3 space-y-1.5 border-t border-white/10 pt-3">
                                    {a.comments.map((c) => (
                                        <div key={c.comment_id} className="text-xs text-slate-400 font-mono">
                                            <span className="text-cyan-500/70">{c.author_username}:</span> {c.body} <span className="text-slate-600">· {timeAgo(c.created_at)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {user && (
                                <form onSubmit={(e) => submitComment(a.answer_id, e)} className="mt-2 flex gap-2">
                                    <input
                                        value={comment[a.answer_id] || ""}
                                        onChange={(e) => setComment((c) => ({ ...c, [a.answer_id]: e.target.value }))}
                                        placeholder="add a comment..."
                                        className="flex-1 bg-transparent border-b border-white/10 text-xs text-slate-300 font-mono py-1 focus:border-cyan-500 focus:outline-none"
                                        data-testid={`comment-input-${a.answer_id}`}
                                    />
                                    <button type="submit" className="text-[10px] uppercase tracking-widest text-cyan-400 hover:text-cyan-300">post</button>
                                </form>
                            )}
                        </div>
                    </article>
                ))}
            </div>

            <div className="pt-6 border-t border-white/10">
                <h3 className="text-lg font-bold tracking-tight mb-3" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>Your answer</h3>
                {qLocked ? (
                    <div className="text-sm text-rose-400 border border-rose-500/30 bg-rose-500/5 p-4 rounded-sm flex items-center gap-2">
                        <Lock size={16} weight="fill" /> This thread is locked. No new answers.
                    </div>
                ) : user ? (
                    <form onSubmit={submitAnswer} className="space-y-3" data-testid="answer-form">
                        <textarea
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                            required
                            placeholder="Markdown supported. Share specifics — config, code, references."
                            className="w-full h-40 bg-[#0b0f19] border border-white/10 text-slate-200 text-sm font-mono p-3 rounded-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 focus:outline-none resize-y"
                            data-testid="answer-input"
                        />
                        <button type="submit" className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm" data-testid="answer-submit-btn">
                            Post answer
                        </button>
                    </form>
                ) : (
                    <div className="text-sm text-slate-400 border border-white/10 p-4 rounded-sm">
                        <Link to="/login" className="text-cyan-400 hover:text-cyan-300">Sign in</Link> to post an answer.
                    </div>
                )}
                <RelatedContent query={q.title} exclude={q.question_id} className="mt-5" />
            </div>
                </div>
                <QuestionSidebar related={data.related || []} contributors={data.contributors || []} tags={q.tags || []} />
            </div>
            <ReportModal
                open={!!reportTarget}
                onClose={() => setReportTarget(null)}
                targetType={reportTarget?.type}
                targetId={reportTarget?.id}
            />
        </div>
    );
}


import { Hash, Question as QuestionIcon, Users as UsersIcon } from "@phosphor-icons/react";

function QuestionSidebar({ related, contributors, tags }) {
    if (!related.length && !contributors.length && !tags.length) return null;
    return (
        <aside className="w-full lg:w-72 shrink-0 space-y-3" data-testid="question-sidebar">
            {tags.length > 0 && (
                <div className="border border-white/10 bg-[#131a2a] rounded-sm">
                    <header className="px-3 py-2 border-b border-white/10 text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                        // tags
                    </header>
                    <div className="p-3 flex flex-wrap gap-1.5">
                        {tags.map((t) => (
                            <Link
                                key={t}
                                to={`/t/${encodeURIComponent(t)}`}
                                data-testid={`question-sidebar-tag-${t}`}
                                className="text-[11px] font-mono text-slate-300 border border-white/10 hover:border-cyan-500/60 hover:text-cyan-300 px-1.5 py-0.5 rounded-sm transition-colors"
                            >
                                #{t}
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {related.length > 0 && (
                <div className="border border-white/10 bg-[#131a2a] rounded-sm">
                    <header className="px-3 py-2 border-b border-white/10 text-[10px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1.5">
                        <QuestionIcon size={11} weight="duotone" className="text-cyan-400" /> related questions
                    </header>
                    <ul className="p-2 space-y-0.5">
                        {related.map((r) => (
                            <li key={r.question_id}>
                                <Link
                                    to={`/q/${r.question_id}`}
                                    data-testid={`question-sidebar-related-${r.question_id}`}
                                    className="block px-2 py-1.5 hover:bg-slate-800/40 rounded-sm group"
                                >
                                    <div className="text-xs font-bold text-slate-200 group-hover:text-cyan-300 line-clamp-2 leading-snug">
                                        {r.title}
                                    </div>
                                    <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                                        {r.score || 0} votes · {r.answer_count || 0} answers · {r.view_count || 0} views
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {contributors.length > 0 && (
                <div className="border border-white/10 bg-[#131a2a] rounded-sm">
                    <header className="px-3 py-2 border-b border-white/10 text-[10px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1.5">
                        <UsersIcon size={11} weight="duotone" className="text-cyan-400" /> top answerers on these tags
                    </header>
                    <ul className="p-2 space-y-0.5">
                        {contributors.map((c) => (
                            <li key={c.user_id}>
                                <Link
                                    to={`/u/${c.username}`}
                                    data-testid={`question-sidebar-contrib-${c.username}`}
                                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-800/40 rounded-sm group"
                                >
                                    {c.picture ? (
                                        <img loading="lazy" decoding="async" src={c.picture} alt="" className="w-6 h-6 rounded-sm border border-white/10" />
                                    ) : (
                                        <div className="w-6 h-6 rounded-sm bg-slate-800 text-[11px] text-cyan-400 flex items-center justify-center font-bold">
                                            {c.username[0].toUpperCase()}
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <div className="text-xs font-mono text-slate-200 group-hover:text-cyan-300 truncate">@{c.username}</div>
                                    </div>
                                    <span className="text-[10px] font-mono text-slate-500">{c.answers}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="border border-cyan-500/30 bg-cyan-500/5 rounded-sm p-3 text-center">
                <Hash size={16} weight="bold" className="text-cyan-400 mx-auto mb-1" />
                <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-mono mb-1">// not finding what you need?</div>
                <Link to="/ask" data-testid="question-sidebar-ask-cta" className="block text-xs font-bold text-slate-100 hover:text-cyan-300">
                    Ask a new question →
                </Link>
            </div>
        </aside>
    );
}
